import React, { useState, useEffect } from "react";
import BitrixAPI from "../api/b24";
import FilePreview from "../components/FileViewer";
import LoadingButton from "../components/LoadingButton";
import FileUpload from "../components/FileUpload";
import { useHeader } from "../contexts/HeaderContext";

const LoftOrdersPageB24 = () => {
  const [orders, setOrders] = useState([]);
  const [workplaceNewOrders, setWorkplaceNewOrders] = useState([]);
  const [workplacePreparationOrders, setWorkplacePreparationOrders] = useState([]);
  const [workplaceClientOrders, setWorkplaceClientOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processingOrders, setProcessingOrders] = useState(new Set());
  const [error, setError] = useState(null);
  const [uploadSuccess, setUploadSuccess] = useState(null);
  const [uploadError, setUploadError] = useState(null);
  const [activeTab, setActiveTab] = useState('loft');
  const [searchQuery, setSearchQuery] = useState("");
  const { setHeaderExtras } = useHeader();

  // Состояние для polling новых заказов
  const [isPollingActive, setIsPollingActive] = useState(true);
  const [newOrdersNotification, setNewOrdersNotification] = useState(null); // {count: number, orderNumbers: string[]}

  // Компонент свайп-кнопки
  const SwipeToConfirmButton = ({ orderId, onConfirm, isLoading }) => {
    const [dragX, setDragX] = useState(0);
    const [isDragging, setIsDragging] = useState(false);
    const [isCompleted, setIsCompleted] = useState(false);
    const [startX, setStartX] = useState(0);
    const maxDrag = 200; // Максимальное расстояние для свайпа
    const threshold = 150; // Пороговое значение для активации
    const sliderRef = React.useRef(null);
    
    const handleSliderMouseDown = (e) => {
      setIsDragging(true);
      setStartX(e.clientX);
      e.preventDefault();
      e.stopPropagation();
    };
    
    const handleSliderTouchStart = (e) => {
      setIsDragging(true);
      setStartX(e.touches[0].clientX);
      e.preventDefault();
      e.stopPropagation();
    };
    
    const handleGlobalMouseMove = (e) => {
      if (!isDragging) return;
      const deltaX = e.clientX - startX;
      setDragX(Math.max(0, Math.min(deltaX, maxDrag)));
      e.preventDefault();
    };
    
    const handleGlobalTouchMove = (e) => {
      if (!isDragging) return;
      const deltaX = e.touches[0].clientX - startX;
      setDragX(Math.max(0, Math.min(deltaX, maxDrag)));
      e.preventDefault();
    };
    
    const handleEnd = () => {
      if (dragX >= threshold && !isCompleted) {
        setIsCompleted(true);
        onConfirm(orderId);
      }
      setIsDragging(false);
      setDragX(0);
      setStartX(0);
    };
    
    // Добавляем глобальные обработчики только при перетаскивании
    React.useEffect(() => {
      if (isDragging) {
        document.addEventListener('mousemove', handleGlobalMouseMove, { passive: false });
        document.addEventListener('mouseup', handleEnd);
        document.addEventListener('touchmove', handleGlobalTouchMove, { passive: false });
        document.addEventListener('touchend', handleEnd);
        
        return () => {
          document.removeEventListener('mousemove', handleGlobalMouseMove);
          document.removeEventListener('mouseup', handleEnd);
          document.removeEventListener('touchmove', handleGlobalTouchMove);
          document.removeEventListener('touchend', handleEnd);
        };
      }
    }, [isDragging, startX, dragX, threshold, isCompleted]);
    
    const progress = (dragX / maxDrag) * 100;
    const isActivated = dragX >= threshold;
    
    return (
      <div className="relative w-full h-16 sm:h-12 bg-gray-200 rounded-lg overflow-hidden select-none pointer-events-none">
        {/* Прогресс-бар */}
        <div 
          className={`absolute inset-y-0 left-0 transition-colors duration-200 pointer-events-none ${
            isActivated ? 'bg-green-400' : 'bg-blue-400'
          }`}
          style={{ width: `${progress}%` }}
        />
        
        {/* Слайдер - только здесь можно начать перетаскивание */}
        <div 
          ref={sliderRef}
          className={`absolute top-1 left-1 w-14 h-14 sm:w-10 sm:h-10 rounded-md shadow-md transition-colors duration-200 flex items-center justify-center cursor-grab pointer-events-auto ${
            isActivated ? 'bg-green-600' : 'bg-blue-600'
          } ${isDragging ? 'cursor-grabbing' : ''}`}
          style={{ 
            transform: `translateX(${dragX}px)`,
            transition: isDragging ? 'none' : 'transform 0.3s ease'
          }}
          onMouseDown={handleSliderMouseDown}
          onTouchStart={handleSliderTouchStart}
        >
          {isLoading ? (
            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
          ) : (
            <span className="text-white text-xs">→</span>
          )}
        </div>
        
        {/* Текст */}
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <span className={`text-sm font-medium transition-colors duration-200 ${
            isActivated ? 'text-white' : 'text-gray-600'
          }`}>
            {isLoading ? 'Обработка...' : isActivated ? 'Отпустите для завершения' : 'Свайп для завершения →'}
          </span>
        </div>
      </div>
    );
  };

  const handleStageUpdate = async (orderId) => {
    try {
      setProcessingOrders(prev => new Set([...prev, orderId]));
      setError(null);
      
      const result = await BitrixAPI.updateStageloft(orderId);
      
      if (result && result.success) {
        setOrders(prevOrders => prevOrders.filter(order => order.id !== orderId));
        console.log(`Заказ ${orderId} успешно переведен из лофта`);
      } else {
        throw new Error(result?.message || "Не удалось обновить стадию");
      }
    } catch (error) {
      console.error("Ошибка при обновлении стадии:", error);
      setError(`Ошибка при обновлении заказа ${orderId}: ${error.message}`);
      alert(`Ошибка: ${error.message}`);
    } finally {
      setProcessingOrders(prev => {
        const newSet = new Set(prev);
        newSet.delete(orderId);
        return newSet;
      });
    }
  };

  const handleWorkplaceStageUpdate = async (orderId, targetStage) => {
    try {
      setProcessingOrders(prev => new Set([...prev, orderId]));
      setError(null);
      
      const result = await BitrixAPI.updateStageworkplace(orderId, targetStage);
      
      if (result && result.success) {
        // Обновляем соответствующий список в зависимости от текущей вкладки
        if (activeTab === 'workplaceNew') {
          setWorkplaceNewOrders(prevOrders => prevOrders.filter(order => order.id !== orderId));
        } else if (activeTab === 'workplacePreparation') {
          setWorkplacePreparationOrders(prevOrders => prevOrders.filter(order => order.id !== orderId));
        }
        console.log(`Заказ ${orderId} успешно переведен на рабочем месте`);
      } else {
        throw new Error(result?.message || "Не удалось обновить стадию");
      }
    } catch (error) {
      console.error("Ошибка при обновлении стадии рабочего места:", error);
      setError(`Ошибка при обновлении заказа ${orderId}: ${error.message}`);
      alert(`Ошибка: ${error.message}`);
    } finally {
      setProcessingOrders(prev => {
        const newSet = new Set(prev);
        newSet.delete(orderId);
        return newSet;
      });
    }
  };

  const handleUploadSuccess = (message) => {
    setUploadSuccess(message);
    setUploadError(null);
    // Автоматически скрыть уведомление через 5 секунд
    setTimeout(() => setUploadSuccess(null), 5000);
  };

  const handleUploadError = (message) => {
    setUploadError(message);
    setUploadSuccess(null);
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setError(null);
  };

  // Функция для загрузки заказов (выделена для повторного использования)
  const fetchOrders = async (isInitialLoad = false) => {
    try {
      if (isInitialLoad) {
        setLoading(true);
      }
      setError(null);

      let response;
      let currentOrders;
      let updateFunction;

      if (activeTab === 'loft') {
        response = await BitrixAPI.getActiveOrdersloft();
        currentOrders = orders;
        updateFunction = setOrders;
      } else if (activeTab === 'workplaceNew') {
        response = await BitrixAPI.getActiveOrdersworkplace('DT1036_14:NEW');
        currentOrders = workplaceNewOrders;
        updateFunction = setWorkplaceNewOrders;
      } else if (activeTab === 'workplacePreparation') {
        response = await BitrixAPI.getActiveOrdersworkplace('DT1036_14:PREPARATION');
        currentOrders = workplacePreparationOrders;
        updateFunction = setWorkplacePreparationOrders;
      } else if (activeTab === 'workplaceClient') {
        response = await BitrixAPI.getActiveOrdersworkplace('DT1036_14:CLIENT');
        currentOrders = workplaceClientOrders;
        updateFunction = setWorkplaceClientOrders;
      }

      if (response && Array.isArray(response)) {
        // Определяем новые заказы (которых еще нет в текущем списке)
        const newOrders = response.filter(newOrder =>
          !currentOrders.some(existingOrder => existingOrder.id === newOrder.id)
        );

        if (newOrders.length > 0 && !isInitialLoad) {
          console.log(`✨ Обнаружено ${newOrders.length} новых заказов лофта (${activeTab}):`, newOrders.map(o => o.id));

          // Показываем уведомление о новых заказах
          setNewOrdersNotification({
            count: newOrders.length,
            orderNumbers: newOrders.map(o => o.orderNumber || o.id)
          });

          // Воспроизводим звук уведомления (если доступен)
          try {
            const audio = new Audio('data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdJivrJBhNjVgodDbq2EcBj+a2/LDciUFLIHO8tiJNwgZaLvt559NEAxQp+PwtmMcBjiR1/LMeSwFJHfH8N2QQAoUXrTp66hVFApGn+DyvmwhBTGH0fPTgjMGHm7A7+OZURE=');
            audio.play().catch(err => console.log('Не удалось воспроизвести звук:', err));
          } catch (err) {
            console.log('Ошибка создания аудио:', err);
          }

          // Автоматически скрываем уведомление через 10 секунд
          setTimeout(() => {
            setNewOrdersNotification(null);
          }, 10000);
        }

        updateFunction(response || []);
      }
    } catch (err) {
      console.error("Ошибка загрузки заказов:", err);
      setError(`Ошибка загрузки заказов: ${err.message}`);
    } finally {
      if (isInitialLoad) {
        setLoading(false);
      }
    }
  };

  // Вставка поиска и счётчика в хедер
  useEffect(() => {
    const currentOrders =
      activeTab === 'loft' ? orders :
      activeTab === 'workplaceNew' ? workplaceNewOrders :
      activeTab === 'workplacePreparation' ? workplacePreparationOrders :
      workplaceClientOrders;
    setHeaderExtras(
      <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
        <input
          type="text"
          placeholder="Поиск по номеру, названию..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
          style={{ width: "220px" }}
        />
        <span className="text-sm text-gray-600">
          Заказов: <strong>{currentOrders.length}</strong>
        </span>
      </div>
    );
    return () => setHeaderExtras(null);
  }, [searchQuery, orders.length, workplaceNewOrders.length, workplacePreparationOrders.length, workplaceClientOrders.length, activeTab]);

  // Первоначальная загрузка заказов при смене вкладки
  useEffect(() => {
    fetchOrders(true);
  }, [activeTab]);

  // Polling для автоматического обновления заказов
  useEffect(() => {
    if (!isPollingActive) {
      return;
    }

    const pollingInterval = setInterval(() => {
      console.log(`🔄 Проверка новых заказов лофта (${activeTab})...`);
      fetchOrders(false);
    }, 30000); // Проверяем каждые 30 секунд

    return () => {
      clearInterval(pollingInterval);
    };
  }, [isPollingActive, activeTab, orders, workplaceNewOrders, workplacePreparationOrders, workplaceClientOrders]);

  // Функция для рендеринга списка заказов
  const renderOrdersList = (ordersList, isWorkplace = false, targetStage = null) => {
    const filteredList = ordersList ? ordersList.filter((order) => {
      if (!searchQuery.trim()) return true;
      const q = searchQuery.toLowerCase();
      return (
        String(order.orderNumber || "").toLowerCase().includes(q) ||
        String(order.title || "").toLowerCase().includes(q) ||
        String(order.id || "").toLowerCase().includes(q)
      );
    }) : [];

    if (!filteredList || filteredList.length === 0) {
      return (
        <div className="bg-white p-4 shadow rounded-lg">
          <p className="text-gray-600">Нет заказов для отображения</p>
        </div>
      );
    }

    return (
      <div className="space-y-4">
        {filteredList.map((order) => (
          <div 
            key={order.id} 
            className="bg-white p-4 shadow rounded-lg transform transition-all duration-300 hover:shadow-lg"
          >
            <div className="flex justify-between items-center mb-4">
              <div>
                <h2 className="text-lg font-medium">Заказ №{order.orderNumber}</h2>
                <p className="text-gray-600">{order.title}</p>
                <p className="text-xs text-gray-500">ID: {order.id}</p>
              </div>
            </div>

            {order.productionFiles && order.productionFiles.length > 0 && (
              <div className="mt-4">
                <h3 className="text-sm text-gray-800 mb-2">Чертежи для производства:</h3>
                {order.productionFiles.map((file) => (
                  <FilePreview 
                    key={file.id} 
                    file={file} 
                    dealId={order.id}
                  />
                ))}
              </div>
            )}

            {order.files && order.files.length > 0 && (
              <div className="mt-4">
                <h3 className="text-sm text-gray-800 mb-2">Деталировка к чертежу:</h3>
                {order.files.map((file) => (
                  <FilePreview
                    key={file.id}
                    file={file}
                    dealId={order.id}
                  />
                ))}
              </div>
            )}

            {order.loftFiles && order.loftFiles.length > 0 && (
              <div className="mt-4">
                <h3 className="text-sm text-gray-800 mb-2">Фото лофта:</h3>
                {order.loftFiles.map((file) => (
                  <FilePreview
                    key={file.id}
                    file={file}
                    dealId={order.id}
                  />
                ))}
              </div>
            )}

            {/* Компонент загрузки файлов */}
            <FileUpload 
              orderId={order.id}
              onUploadSuccess={handleUploadSuccess}
              onUploadError={handleUploadError}
              uploadEndpoint={isWorkplace ? "upload-workplace-file" : "upload-loft-file"}
              fieldType={isWorkplace ? "workplace" : "loft"}
            />
            
            {/* Свайп-кнопка во всю ширину блока */}
            {targetStage && (
              <div className="mt-4">
                <SwipeToConfirmButton
                  orderId={order.id}
                  onConfirm={isWorkplace ? 
                    (id) => handleWorkplaceStageUpdate(id, targetStage) : 
                    handleStageUpdate
                  }
                  isLoading={processingOrders.has(order.id)}
                />
              </div>
            )}
          </div>
        ))}
      </div>
    );
  };

  if (loading) return <div className="p-4">Ойланып жатырмын ...</div>;

  return (
    <div className="container mx-auto p-4">
      

      

      {/* Вкладки */}
      <div className="mb-6 border-b border-gray-200">
        <ul className="flex flex-wrap -mb-px">
          <li className="mr-2">
            <button
              className={`inline-block p-4 text-sm font-medium ${
                activeTab === "loft"
                  ? "text-blue-600 border-b-2 border-blue-600"
                  : "text-gray-500 hover:text-gray-700"
              }`}
              onClick={() => handleTabChange("loft")}
            >
              Сборка Лофта
            </button>
          </li>
          <li className="mr-2">
            <button
              className={`inline-block p-4 text-sm font-medium ${
                activeTab === "workplaceNew"
                  ? "text-blue-600 border-b-2 border-blue-600"
                  : "text-gray-500 hover:text-gray-700"
              }`}
              onClick={() => handleTabChange("workplaceNew")}
            >
              Нужно закупить
            </button>
          </li>
          <li className="mr-2">
            <button
              className={`inline-block p-4 text-sm font-medium ${
                activeTab === "workplacePreparation"
                  ? "text-blue-600 border-b-2 border-blue-600"
                  : "text-gray-500 hover:text-gray-700"
              }`}
              onClick={() => handleTabChange("workplacePreparation")}
            >
              Закуп
            </button>
          </li>
          <li className="mr-2">
            <button
              className={`inline-block p-4 text-sm font-medium ${
                activeTab === "workplaceClient"
                  ? "text-blue-600 border-b-2 border-blue-600"
                  : "text-gray-500 hover:text-gray-700"
              }`}
              onClick={() => handleTabChange("workplaceClient")}
            >
              Закуп готов
            </button>
          </li>
        </ul>
      </div>

      {/* Уведомление о новых заказах */}
      {newOrdersNotification && (
        <div className="bg-green-50 border-2 border-green-400 text-green-800 px-4 py-3 rounded-lg mb-4 shadow-lg animate-pulse">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <div>
                <strong className="text-lg">Поступило {newOrdersNotification.count} новых заказов лофта!</strong>
                <div className="text-sm text-green-700 mt-1">
                  Заказы: {newOrdersNotification.orderNumbers.join(', ')}
                </div>
              </div>
            </div>
            <button
              onClick={() => setNewOrdersNotification(null)}
              className="text-green-600 hover:text-green-800 transition-colors"
            >
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
              </svg>
            </button>
          </div>
        </div>
      )}

      {/* Отображение ошибок */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-4">
          <strong>Ошибка:</strong> {error}
          <button 
            onClick={() => setError(null)}
            className="ml-2 text-red-500 hover:text-red-700"
          >
            ✕
          </button>
        </div>
      )}

      {/* Уведомления о загрузке файлов */}
      {uploadSuccess && (
        <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded mb-4">
          <strong>Успех:</strong> {uploadSuccess}
          <button 
            onClick={() => setUploadSuccess(null)}
            className="ml-2 text-green-500 hover:text-green-700"
          >
            ✕
          </button>
        </div>
      )}

      {uploadError && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-4">
          <strong>Ошибка загрузки:</strong> {uploadError}
          <button 
            onClick={() => setUploadError(null)}
            className="ml-2 text-red-500 hover:text-red-700"
          >
            ✕
          </button>
        </div>
      )}
      
      {/* Содержимое вкладок */}
      <>
        {activeTab === "loft" && renderOrdersList(orders, false, true)}
        {activeTab === "workplaceNew" && renderOrdersList(workplaceNewOrders, true, "preparation")}
        {activeTab === "workplacePreparation" && renderOrdersList(workplacePreparationOrders, true, "client")}
        {activeTab === "workplaceClient" && renderOrdersList(workplaceClientOrders, true, null)}
      </>
    </div>
  );
};

export default LoftOrdersPageB24;